import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Close as CloseIcon,
  Send as SendIcon,
  ContentCopy as NewDocIcon,
  Edit as ReplaceIcon,
  ShortText as ConciseIcon,
  Subject as DetailedIcon,
  Gavel as FormalIcon,
  Psychology as CreativeIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface AIFullRewriteDialogProps {
  open: boolean;
  onClose: () => void;
  documentContent: string;
  documentTitle?: string;
  onRewrite: (newContent: string, createNew: boolean) => void;
}

// Estilos de reescrita
const rewriteStyles = [
  { 
    id: 'concise', 
    label: 'Mais Conciso', 
    icon: <ConciseIcon />, 
    description: 'Reduz o texto mantendo informações essenciais',
    prompt: 'Reescreva este documento jurídico de forma mais concisa e objetiva. Mantenha todas as informações legais essenciais, mas remova redundâncias e seja mais direto. Preserve a estrutura formal do documento.'
  },
  { 
    id: 'detailed', 
    label: 'Mais Detalhado', 
    icon: <DetailedIcon />, 
    description: 'Expande com mais argumentos e fundamentação',
    prompt: 'Expanda este documento jurídico com mais detalhes, argumentação e fundamentação legal. Adicione mais referências a leis e jurisprudências quando pertinente. Mantenha a estrutura formal.'
  },
  { 
    id: 'formal', 
    label: 'Mais Formal', 
    icon: <FormalIcon />, 
    description: 'Linguagem técnica e formal adequada',
    prompt: 'Reescreva este documento utilizando linguagem mais formal, técnica e adequada ao meio jurídico brasileiro. Utilize termos técnicos apropriados e estrutura formal de petições.'
  },
  { 
    id: 'custom', 
    label: 'Personalizado', 
    icon: <CreativeIcon />, 
    description: 'Escreva sua própria instrução',
    prompt: ''
  },
];

const AIFullRewriteDialog: React.FC<AIFullRewriteDialogProps> = ({
  open,
  onClose,
  documentContent,
  documentTitle,
  onRewrite,
}) => {
  const [selectedStyle, setSelectedStyle] = useState<string>('concise');
  const [customInstruction, setCustomInstruction] = useState('');
  const [outputOption, setOutputOption] = useState<'replace' | 'new'>('replace');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [step, setStep] = useState<'config' | 'preview'>('config');

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    const style = rewriteStyles.find(s => s.id === selectedStyle);
    const instruction = selectedStyle === 'custom' ? customInstruction : style?.prompt || '';

    if (!instruction.trim()) {
      setError('Por favor, escreva uma instrução para a IA.');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/ai/rewrite-full-document', {
        document_content: documentContent,
        document_title: documentTitle,
        instruction: instruction,
      });

      if (response.data.rewritten_content) {
        setPreviewContent(response.data.rewritten_content);
        setStep('preview');
      } else {
        setError('Não foi possível reescrever o documento. Tente novamente.');
      }
    } catch (err: any) {
      console.error('Erro ao reescrever documento:', err);
      setError(err.response?.data?.detail || 'Erro ao processar a solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (previewContent) {
      onRewrite(previewContent, outputOption === 'new');
      handleClose();
    }
  };

  const handleClose = () => {
    setStep('config');
    setPreviewContent(null);
    setError(null);
    setSelectedStyle('concise');
    setCustomInstruction('');
    setOutputOption('replace');
    onClose();
  };

  const handleBack = () => {
    setStep('config');
    setPreviewContent(null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          maxHeight: '90vh',
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'white' }}>
          <AIIcon sx={{ fontSize: 28, color: 'white' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
              Reescrever Documento com IA
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, color: 'rgba(255,255,255,0.9)' }}>
              {step === 'config' ? 'Configure como deseja reescrever' : 'Revise o resultado'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {step === 'config' && (
          <Box sx={{ p: 3 }}>
            {/* Info do documento */}
            <Alert severity="info" sx={{ mb: 3 }}>
              O documento atual tem aproximadamente <strong>{Math.round(documentContent.length / 5)} palavras</strong>.
              A IA irá reescrever todo o conteúdo mantendo a estrutura e informações essenciais.
            </Alert>

            {/* Estilos de reescrita */}
            <Typography variant="subtitle1" fontWeight={600} color="#1a365d" gutterBottom>
              Como você quer reescrever?
            </Typography>
            
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {rewriteStyles.map((style) => (
                <Box
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  sx={{
                    p: 2,
                    border: '2px solid',
                    borderColor: selectedStyle === style.id ? '#1a365d' : '#e2e8f0',
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: selectedStyle === style.id ? '#f0f4f8' : 'white',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#1a365d',
                      bgcolor: '#f8fafc',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ color: selectedStyle === style.id ? '#1a365d' : '#64748b' }}>
                      {style.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600} color={selectedStyle === style.id ? '#1a365d' : '#334155'}>
                        {style.label}
                      </Typography>
                      <Typography variant="body2" color="#64748b">
                        {style.description}
                      </Typography>
                    </Box>
                    {selectedStyle === style.id && (
                      <Chip label="Selecionado" size="small" color="primary" />
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>

            {/* Campo personalizado */}
            {selectedStyle === 'custom' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Ex: Reescreva de forma mais objetiva, focando nos pedidos principais e removendo argumentos repetitivos..."
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            )}

            <Divider sx={{ my: 3 }} />

            {/* Opção de output */}
            <FormControl component="fieldset">
              <FormLabel 
                component="legend" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1a365d',
                  '&.Mui-focused': { color: '#1a365d' },
                }}
              >
                O que fazer com o resultado?
              </FormLabel>
              <RadioGroup
                value={outputOption}
                onChange={(e) => setOutputOption(e.target.value as 'replace' | 'new')}
              >
                <FormControlLabel
                  value="replace"
                  control={<Radio sx={{ color: '#1a365d', '&.Mui-checked': { color: '#1a365d' } }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReplaceIcon fontSize="small" />
                      <span>Substituir o documento atual</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="new"
                  control={<Radio sx={{ color: '#1a365d', '&.Mui-checked': { color: '#1a365d' } }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NewDocIcon fontSize="small" />
                      <span>Criar um novo documento (mantém o original)</span>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {/* Erro */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}

        {step === 'preview' && previewContent && (
          <Box sx={{ p: 3 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Documento reescrito com sucesso! Revise o conteúdo abaixo antes de aplicar.
            </Alert>
            
            <Typography variant="subtitle2" color="#64748b" gutterBottom>
              Preview do documento reescrito:
            </Typography>
            
            <Box
              sx={{
                p: 3,
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                maxHeight: 400,
                overflow: 'auto',
                '& p': { mb: 1.5 },
              }}
              dangerouslySetInnerHTML={{ __html: previewContent }}
            />
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'rgba(255,255,255,0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}>
            <CircularProgress size={48} sx={{ color: '#1a365d', mb: 2 }} />
            <Typography variant="h6" color="#1a365d">
              Reescrevendo documento...
            </Typography>
            <Typography variant="body2" color="#64748b">
              Isso pode levar alguns segundos
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        {step === 'config' ? (
          <>
            <Button onClick={handleClose} sx={{ color: '#64748b' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading || (selectedStyle === 'custom' && !customInstruction.trim())}
              startIcon={<AIIcon />}
              sx={{
                bgcolor: '#1a365d',
                px: 3,
                '&:hover': { bgcolor: '#2d5a87' },
              }}
            >
              Gerar com IA
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleBack} sx={{ color: '#64748b' }}>
              Voltar e ajustar
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              startIcon={outputOption === 'new' ? <NewDocIcon /> : <ReplaceIcon />}
              sx={{
                bgcolor: '#16a34a',
                px: 3,
                '&:hover': { bgcolor: '#15803d' },
              }}
            >
              {outputOption === 'new' ? 'Criar Novo Documento' : 'Substituir Documento'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIFullRewriteDialog;

